import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { dashboardService } from '../../../services/dashboard.service';
import { useProduct } from '../../../contexts/ProductContext';
import { ChartPanel } from '../../../components/atlas/ChartPanel';
import { useChartColors, useChartPalette } from '../../../utils/chartTokens';
import { Skeleton, EmptyState } from '../../../ui';
import type { Period } from '../../../components/atlas/PeriodSelector';

function periodToMonths(p: Period): number {
  if (p === '30d') {
    return 1;
  }
  if (p === '90d') {
    return 3;
  }
  if (p === '6m') {
    return 6;
  }
  return 12;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

interface Props {
  period: Period;
}

export function VendasTab({ period }: Props) {
  const { selectedProductId } = useProduct();
  const months = periodToMonths(period);
  const colors = useChartColors();
  const palette = useChartPalette();

  const params = { productId: selectedProductId ?? undefined, months };

  const conv = useQuery({
    queryKey: ['dashboard', 'conversion', params],
    queryFn: () => dashboardService.getConversion(params.productId, months),
    enabled: !!selectedProductId,
  });
  const pipeline = useQuery({
    queryKey: ['dashboard', 'pipeline', params],
    queryFn: () => dashboardService.getPipeline(params.productId),
    enabled: !!selectedProductId,
  });
  const revenue = useQuery({
    queryKey: ['dashboard', 'revenue', params],
    queryFn: () => dashboardService.getRevenue(params.productId, months),
    enabled: !!selectedProductId,
  });
  const lost = useQuery({
    queryKey: ['dashboard', 'lost', params],
    queryFn: () => dashboardService.getLost(params.productId, months),
    enabled: !!selectedProductId,
  });

  if (!selectedProductId) {
    return (
      <EmptyState
        title="Selecione um produto"
        body="Insights de vendas dependem de um produto selecionado."
      />
    );
  }

  const funnelData = ((pipeline.data as any)?.data ?? []).map((s: any) => ({
    name:
      typeof s.stageName === 'string' && s.stageName.length > 14
        ? `${s.stageName.slice(0, 14)}…`
        : (s.stageName ?? '—'),
    leads: s.count ?? 0,
  }));

  const conversionMonthly = ((conv.data as any)?.data ?? []).map((m: any) => ({
    month: m.period,
    taxa: Number((m.conversionRate ?? 0).toFixed(1)),
  }));

  const revenueMonthly = ((revenue.data as any)?.data ?? []).map((m: any) => ({
    month: m.period,
    total: m.mrr ?? 0,
    novos: m.newDealsValue ?? 0,
  }));

  const lostByReason = ((lost.data as any)?.data?.reasons ?? []).map((r: any) => ({
    name: r.reason || 'Sem motivo',
    value: r.count ?? 0,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartPanel title="Funil de conversão" subtitle="Distribuição de leads por stage">
        {pipeline.isLoading ? (
          <Skeleton height={280} />
        ) : funnelData.length === 0 ? (
          <EmptyState title="Sem dados" body="Ainda não há leads no funil." />
        ) : (
          <ResponsiveContainer>
            <BarChart data={funnelData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis dataKey="name" stroke={colors.inkTer} fontSize={11} />
              <YAxis allowDecimals={false} stroke={colors.inkTer} fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: colors.ink }}
                itemStyle={{ color: colors.inkSec }}
              />
              <Bar dataKey="leads" name="Leads" fill={colors.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ChartPanel title="Taxa de conversão" subtitle="Evolução mensal (%)">
        {conv.isLoading ? (
          <Skeleton height={280} />
        ) : conversionMonthly.length === 0 ? (
          <EmptyState title="Sem dados" body="Ainda não há série temporal de conversão." />
        ) : (
          <ResponsiveContainer>
            <LineChart
              data={conversionMonthly}
              margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis dataKey="month" stroke={colors.inkTer} fontSize={11} />
              <YAxis unit="%" domain={[0, 100]} stroke={colors.inkTer} fontSize={11} />
              <Tooltip
                formatter={(value) => [`${value}%`, 'Taxa']}
                contentStyle={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: colors.ink }}
                itemStyle={{ color: colors.inkSec }}
              />
              <Line
                type="monotone"
                dataKey="taxa"
                name="Taxa de conversão"
                stroke={colors.accent}
                strokeWidth={2}
                dot={{ fill: colors.accent, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ChartPanel title="Receita por mês" subtitle="Total e novos negócios">
        {revenue.isLoading ? (
          <Skeleton height={280} />
        ) : revenueMonthly.length === 0 ? (
          <EmptyState title="Sem dados" body="Ainda não há receita registrada." />
        ) : (
          <ResponsiveContainer>
            <BarChart data={revenueMonthly} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis dataKey="month" stroke={colors.inkTer} fontSize={11} />
              <YAxis
                tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                stroke={colors.inkTer}
                fontSize={11}
              />
              <Tooltip
                formatter={(value) => [formatCurrency(Number(value))]}
                contentStyle={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: colors.ink }}
                itemStyle={{ color: colors.inkSec }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: colors.inkSec }} />
              <Bar dataKey="total" name="Total" fill={colors.warning} radius={[4, 4, 0, 0]} />
              <Bar dataKey="novos" name="Novos Negócios" fill={colors.info} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ChartPanel title="Razões de perda" subtitle="Top motivos">
        {lost.isLoading ? (
          <Skeleton height={280} />
        ) : lostByReason.length === 0 ? (
          <EmptyState title="Sem dados" body="Ainda não há leads perdidos com motivo." />
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={lostByReason}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }: { name?: string; percent?: number }) =>
                  `${(name ?? '').length > 12 ? `${(name ?? '').slice(0, 12)}..` : (name ?? '')} ${((percent ?? 0) * 100).toFixed(0)}%`
                }
                labelLine={{ strokeWidth: 1 }}
                fontSize={11}
              >
                {lostByReason.map((_: any, i: number) => (
                  <Cell key={i} fill={palette[i % palette.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [value, 'Leads']}
                contentStyle={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, color: colors.inkSec }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>
    </div>
  );
}
